/**
 * Project Color Constants
 *
 * Centralized color definitions based on the project's theme
 * Defined in: src/_metronic/assets/sass/components/_variables.custom.scss
 *
 * Usage:
 * import { CHART_COLORS, THEME_COLORS } from './colors'
 *
 * Example:
 * colors: [CHART_COLORS.PRIMARY, CHART_COLORS.SUCCESS]
 */

// Main theme colors matching SASS variables
export const THEME_COLORS = {
  // Primary colors
  PRIMARY: '#7950f2', // Primary Purple - Main brand color
  PRIMARY_LIGHT: '#f3f0fb', // Light purple background
  PRIMARY_ACTIVE: '#7950f2', // Primary active state

  // Success colors
  SUCCESS: '#50cd89', // Success Green - Positive metrics
  SUCCESS_LIGHT: '#e8fff3', // Light green background
  SUCCESS_ACTIVE: '#47be7d', // Success active state

  // Info colors
  INFO: '#7239ea', // Info Purple - Alternative info color
  INFO_LIGHT: '#f8f5ff', // Light info background
  INFO_ACTIVE: '#5014d0', // Info active state

  // Warning colors
  WARNING: '#ffc700', // Warning Yellow - Attention metrics
  WARNING_LIGHT: '#fff8dd', // Light warning background
  WARNING_ACTIVE: '#f1bc00', // Warning active state

  // Danger colors
  DANGER: '#f1416c', // Danger Red - Error states, negative metrics
  DANGER_LIGHT: '#fff5f8', // Light danger background
  DANGER_ACTIVE: '#d9214e', // Danger active state

  // Secondary/Info Blue
  INFO_BLUE: '#50b5ff', // Info Blue - Secondary emphasis, informational data

  // Gray scale
  GRAY_100: '#f5f8fa',
  GRAY_200: '#eff2f5',
  GRAY_300: '#e4e6ef',
  GRAY_400: '#b5b5c3',
  GRAY_500: '#a1a5b7', // Text muted
  GRAY_600: '#7e8299',
  GRAY_700: '#5e6278',
  GRAY_800: '#3f4254',
  GRAY_900: '#181c32', // Dark text

  // Basic colors
  WHITE: '#ffffff',
  BLACK: '#000000',
} as const

// Chart-specific color palette
export const CHART_COLORS = {
  // Primary chart colors (most commonly used in charts)
  PRIMARY: THEME_COLORS.PRIMARY, // Purple
  SUCCESS: THEME_COLORS.SUCCESS, // Green
  INFO_BLUE: THEME_COLORS.INFO_BLUE, // Blue
  WARNING: THEME_COLORS.WARNING, // Yellow
  DANGER: THEME_COLORS.DANGER, // Red
  INFO: THEME_COLORS.INFO, // Info Purple

  // Chart utility colors
  NEUTRAL: THEME_COLORS.GRAY_500, // For neutral/secondary data
  BACKGROUND: THEME_COLORS.WHITE,
  BORDER: THEME_COLORS.GRAY_300,
  GRID: '#e5e7eb', // Grid lines
  TEXT: THEME_COLORS.GRAY_900,
  TEXT_MUTED: THEME_COLORS.GRAY_600,
  NO_DATA_TEXT: '#a6a6b1', // Text for "no data" messages
} as const

// Predefined color arrays for multi-series charts
export const CHART_COLOR_PALETTES = {
  // Default palette - ordered by visual hierarchy
  DEFAULT: [
    CHART_COLORS.PRIMARY, // Purple
    CHART_COLORS.SUCCESS, // Green
    CHART_COLORS.WARNING, // Yellow
    CHART_COLORS.INFO_BLUE, // Blue
    CHART_COLORS.DANGER, // Red
    CHART_COLORS.INFO, // Info Purple
    CHART_COLORS.NEUTRAL, // Gray
  ] as string[],

  // Two-color palettes for comparison charts
  COMPARISON_TWO: [CHART_COLORS.DANGER, CHART_COLORS.INFO_BLUE] as string[],

  // Three-color palette for promote actions / basic metrics
  PROMOTE_ACTIONS: [CHART_COLORS.PRIMARY, CHART_COLORS.SUCCESS, CHART_COLORS.WARNING] as string[],

  // Leaderboard ranking colors
  LEADERBOARD: {
    FIRST: CHART_COLORS.PRIMARY, // Purple for 1st place
    SECOND: CHART_COLORS.SUCCESS, // Green for 2nd place
    THIRD: CHART_COLORS.WARNING, // Yellow for 3rd place
    OTHER: CHART_COLORS.INFO_BLUE, // Blue for others
  },

  // Metric-specific colors
  METRICS: {
    NEW_LEADS: CHART_COLORS.PRIMARY, // Purple
    FROM_WALLET: CHART_COLORS.INFO_BLUE, // Blue
    FROM_CONTACT: CHART_COLORS.SUCCESS, // Green
    SCAN_WALLET_CARD: CHART_COLORS.WARNING, // Yellow
    VIA_WALLET: CHART_COLORS.DANGER, // Red
    VIA_CONTACT: CHART_COLORS.INFO_BLUE, // Blue
  },
} as const

// Legend colors for specific chart types
export const LEGEND_COLORS = {
  WALLET: CHART_COLORS.DANGER, // Red
  CONTACT: CHART_COLORS.INFO_BLUE, // Blue
} as const

// Helper function to get color array for leaderboard
export const getLeaderboardColors = (dataLength: number): string[] => {
  return Array.from({length: dataLength}, (_, index) => {
    if (index === 0) return CHART_COLOR_PALETTES.LEADERBOARD.FIRST
    if (index === 1) return CHART_COLOR_PALETTES.LEADERBOARD.SECOND
    if (index === 2) return CHART_COLOR_PALETTES.LEADERBOARD.THIRD
    return CHART_COLOR_PALETTES.LEADERBOARD.OTHER
  })
}

// Export all as a single object for convenience
export const COLORS = {
  THEME: THEME_COLORS,
  CHART: CHART_COLORS,
  PALETTES: CHART_COLOR_PALETTES,
  LEGEND: LEGEND_COLORS,
} as const

// Type exports for TypeScript support
export type ThemeColor = typeof THEME_COLORS[keyof typeof THEME_COLORS]
export type ChartColor = typeof CHART_COLORS[keyof typeof CHART_COLORS]
export type ColorPalette = typeof CHART_COLOR_PALETTES[keyof typeof CHART_COLOR_PALETTES]
